/**
 * Generic reusable web component wrapping the Slyde carousel library.
 * All configuration is read from data attributes on the host element.
 *
 * @example
 * <slyde-show data-loop data-pagination data-autoplay="5000">
 *   <div class="slyde-container">
 *     <div class="slyde-wrapper">
 *       <!-- slides -->
 *     </div>
 *     <div class="slyde-pagination"></div>
 *   </div>
 * </slyde-show>
 */
if (!customElements.get('slyde-show')) {
  class SlydeShow extends HTMLElement {
    #slyde = null;

    static get observedAttributes() {
      return ['data-disabled'];
    }

    // Public getter to access the Slyde instance
    get slyde() {
      return this.#slyde;
    }

    // Public methods to control slideshow
    next() {
      this.#slyde?.next();
    }

    prev() {
      this.#slyde?.prev();
    }

    goTo(index) {
      this.#slyde?.goTo(index);
    }

    connectedCallback() {
      if (this.hasAttribute('data-disabled')) return;

      if (typeof window.Slyde !== 'undefined') {
        this.#init();
        return;
      }

      const script = document.querySelector('script[src*="slyde.umd"]');
      if (script) {
        script.addEventListener('load', () => this.#init(), { once: true });
      }
    }

    disconnectedCallback() {
      this.#destroy();
    }

    attributeChangedCallback(name) {
      if (name === 'data-disabled') {
        if (this.hasAttribute('data-disabled')) {
          this.#destroy();
        } else {
          this.#init();
        }
      }
    }

    #init() {
      if (this.#slyde || !this.isConnected || this.hasAttribute('data-disabled')) return;
      if (typeof window.Slyde === 'undefined') return;

      const container = this.querySelector('.slyde-container');
      if (!container) return;

      this.#restoreSlides();

      const modules = [];
      const config = {
        loop: this.hasAttribute('data-loop'),
        navigation: false,
        thumbnails: false,
        video: false,
        scrollbar: false,
        pagination: false,
        autoplay: false,
      };

      // Pagination
      if (this.hasAttribute('data-pagination')) {
        modules.push(window.Slyde.Pagination);
        const paginationEl = this.dataset.paginationEl || '.slyde-pagination';
        config.pagination = { el: paginationEl, clickable: true };
      }

      // Navigation
      if (this.hasAttribute('data-navigation')) {
        modules.push(window.Slyde.Navigation);
        config.navigation = true;
      }

      // Scrollbar — needs .slyde-scrollbar-thumb (+ optional
      // .slyde-scrollbar-thumb-ghost for the loop wrap) inside the container.
      // data-scrollbar-thumb-width (px) fixes the thumb size instead of the
      // default proportional slidesPerView/totalSlides width.
      if (this.hasAttribute('data-scrollbar')) {
        modules.push(window.Slyde.Scrollbar);
        const thumbWidth = parseInt(this.dataset.scrollbarThumbWidth || '', 10);
        config.scrollbar = Number.isFinite(thumbWidth) && thumbWidth > 0 ? { thumbWidth } : true;
      }

      // Autoplay
      const autoplayDelay = this.dataset.autoplay;
      if (autoplayDelay) {
        modules.push(window.Slyde.Autoplay);
        const pauseOnHover = this.dataset.pauseOnHover !== 'false';
        config.autoplay = {
          delay: parseInt(autoplayDelay, 10),
          pauseOnHover,
        };
      }

      // Layout
      if (this.dataset.slidesPerView) {
        config.slidesPerView = parseFloat(this.dataset.slidesPerView);
      }
      if (this.dataset.spaceBetween) {
        config.spaceBetween = parseInt(this.dataset.spaceBetween, 10);
      }
      if (this.dataset.padding) {
        // Numeric paddings stay numbers; calc()/px strings pass through
        // (Slyde evaluates calc expressions against the container width).
        const padding = this.dataset.padding;
        config.padding = /^\d+$/.test(padding) ? parseInt(padding, 10) : padding;
      }
      if (this.dataset.clonesPerSide) {
        // Loop clones beyond ceil(slidesPerView) — needed when a padding
        // peek exposes more neighbors than slidesPerView implies.
        config.clonesPerSide = parseInt(this.dataset.clonesPerSide, 10);
      }

      // Breakpoints
      if (this.dataset.breakpoints) {
        try {
          config.breakpoints = JSON.parse(this.dataset.breakpoints);
        } catch (e) {
          console.warn('Slyde: Invalid breakpoints JSON', e);
        }
      }

      // Effect
      if (this.dataset.effect) {
        config.effect = this.dataset.effect;
      }

      config.modules = modules;
      this.#slyde = new window.Slyde.Slyde(container, config);
    }

    #restoreSlides() {
      const wrapper = this.querySelector('.slyde-wrapper');
      if (!wrapper) return;
      while (Array.from(wrapper.children).some((child) => child.classList.contains('slyde-slide'))) {
        for (const slide of Array.from(wrapper.children)) {
          if (!slide.classList.contains('slyde-slide')) continue;
          if (slide.classList.contains('slyde-slide-clone')) {
            slide.remove();
          } else {
            slide.replaceWith(...slide.childNodes);
          }
        }
      }
    }

    #destroy() {
      this.#slyde?.destroy();
      this.#slyde = null;
      this.#restoreSlides();
    }
  }

  customElements.define('slyde-show', SlydeShow);
}
